import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from '@usas/ui-web/wrappers/ReactRouterDom';

import NavLinks from '../../../meet/NavLinks';
import HostNavLinks from '../../HostNavLinks';
import MyMeetsNavLinks from '../../../myMeets/MyMeetsNavLinks';
import HostRegistrationConstants from '../HostRegistrationConstants';

import validate from './OmeEligibilityNonHQValidation';

import useOmeMeetData from '../../../../state/omeMeet/UseOmeMeetData';
import useOmeMeetEntryData from '../../../../state/omeMeetEntry/UseOmeMeetEntryData';
import useOmeMeetDateValidationData from '../../../../state/omeMeetDateValidation/UseOmeMeetDateValidationData';
import useMyMeetsFiltersData from '../../../../state/myMeetsFilters/UseMyMeetsFiltersData';

import useSearchMeetData from '@usas/ui-web/state/searchMeet/UseSearchMeetData';
import useEnvironmentVariableData from '@usas/ui-web/state/environmentVariable/UseEnvironmentVariableData';
import useLeftNavModalData from '@usas/ui-web/state/leftNavModal/UseLeftNavModalData';
import useSelectOrgUnitData from '@usas/ui-web/state/selectOrgUnit/UseSelectOrgUnitData';

import useForm from '@usas/ui-web/utils/UseForm';
import Constants from '@usas/ui-web/utils/Constants';
import ToIntIfInt from '@usas/ui-web/utils/ToIntIfInt';

const INITIAL_VIEW_STATE = {
  submitButton: '',
  tryPutOmeMeetEntry: false,
  putOmeMeetEntryArray: [],
  doNotTryRedirect: false,
  tryRedirect: false,
  omeMeetQualificationId: Constants.DEFAULT_ID,
  qualificationBonusId: Constants.DEFAULT_ID,
  sortedBonusGridData: [],
  sortedCoachGridData: []
};

const INITIAL_FORM_STATE = {
  includeRelaySwimmerForCoachPasses: '',
  maxEventsPerIndividualPerMeet: '',
  maxEventsPerIndividualPerSession: '',
  relayEvents: '',
  allowRelayOnlySwimmer: '',
  relayTeamFromRosterOnly: '',
  forceAggregateIfFasterThanTeamTime: '',
  maxRelayTeamEntriesPerEvent: '',
  maxRelayEventsPerIndividualPerMeet: '',
  bonusEvents: '',
  exclude50FromBonusTotal: '',
  credentialsCanBeAddedAfterCap: '',
  credentialsAfterCapFeeAmount: '',
  useAdjustedSwimTime: ''
};

const INITIAL_MODAL_STATE = {
  displayPopUp: false,
  modalTitle: '',
  minQualifyingEvents: '',
  maxQualifyingEvents: '',
  bonusStandardCount: '',
  omeMeetQualificationId: Constants.DEFAULT_ID,
  qualificationBonusId: Constants.DEFAULT_ID,
  minSwimmers: '',
  maxSwimmers: '',
  coachPasses: '',
  omeMeetCoachPassId: Constants.DEFAULT_ID
};

const INITIAL_WARNING_MODAL_STATE = {
  displayPopUp: false,
  modalTitle: 'Save & Delete Confirmation',
  deleteAllEventBonusTimeStandardCuts: false,
  timeStandardWarningMsg: '',
  deleteCostPerAthleteIndividualBonusEventEntry: false,
  bonusPricingWarningMessage: '',
  deleteRelayRelatedCosts: false,
  relayOnlySwimmerPricingWarningMessage: '',
  relayEntryPricingWarningMessage: '',
  deleteIncludeRelayOnlySwimmersInMaxNumber: false,
  includeRelayOnlySwimmersInMaxNumberWarningMessage: ''
};

const useOmeEligibilityNonHQ = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState(INITIAL_VIEW_STATE);
  const [modalState, setModalState] = useState(INITIAL_MODAL_STATE);
  const [warningModalState, setWarningModalState] = useState(INITIAL_WARNING_MODAL_STATE);
  const { omeMeetState, putOmeMeet } = useOmeMeetData();
  const { omeMeetEntryState, putOmeMeetEntry } = useOmeMeetEntryData();
  const { omeMeetDateValidationState } = useOmeMeetDateValidationData();
  const { searchMeetState, setSearchMeetState } = useSearchMeetData();
  const { selectOrgUnitState } = useSelectOrgUnitData();
  const { myMeetsFiltersState } = useMyMeetsFiltersData();
  const { environmentVariableState, ATHLETE_INDIVIDUAL_BONUS_EVENT_ENTRY_OME_FEE_TYPE_ID, RELAY_ONLY_SWIMMER_OME_FEE_TYPE_ID,
    RELAY_EVENT_ENTRY_OME_FEE_TYPE_ID, MAX_RELAY_TEAM_ENTRIES_PER_EVENT, HIDE_OME_ELIGIBILITY_50_BONUS_OPTION } = useEnvironmentVariableData();
  const { leftNavModalState, setLeftNavModalState } = useLeftNavModalData();
  const { formState, errorState, onFormValueChanged, isSubmitting, handleSubmit, setIsDirty,
    isDirty, setFormData, setErrors } = useForm(INITIAL_FORM_STATE, submitFormCallback, validate);

  const tryValidateBeforeRedirect = async (submitButton, omeMeetQualificationId, qualificationBonusId, omeMeetCoachPassId) => {
    const errors = await validate(formState) || {};
    setErrors(errors);
    //Prevent user from setting relayEvents to false if they have already added relay events
    let relayError = false;
    if (formState.relayEvents === false) {
      if (omeMeetState.objData.meetAgeGroup?.length > 0) {
        for (let i = 0; i < omeMeetState.objData.meetAgeGroup.length; i++) {
          for (let j = 0; j < omeMeetState.objData.meetAgeGroup[i].meetEvent.length; j++) {
            if (omeMeetState.objData.meetAgeGroup[i].meetEvent[j]?.event?.isRelay === true) {
              relayError = true;
            }
          }
        }
      }
    }
    if (relayError === true) {
      setErrors({ ...errorState, error: `Meet has Relay Events? cannot be set to 'No' because relay events have been added on the Events page. To be permitted to set this field to 'No', navigate to the Events page and delete all relay events.` });
    }
    else if (relayError === false) {
      let error = ''
      if (issueWithMaxEventsPerIndividualPerMeet() === true) {
        error = `Max Events Per Individual Per Meet (${formState.maxEventsPerIndividualPerMeet}) cannot be less than the sum of any Bonus Event Rule's Max Number of Qualifying Events and Allowed Number of Bonus Events. Please delete any Bonus Event Rule in conflict with this validation rule in order to save changes made on this page.`
        setErrors({ ...errorState, error: error });
      }
      const issueWithMaxNumberOfAthletesForMeetReturn = issueWithMaxNumberOfAthletesForMeet();
      if (issueWithMaxNumberOfAthletesForMeetReturn?.issue === true) {
        error = error ? error + `\n\n The highest Max Number of Athletes For Meet (${issueWithMaxNumberOfAthletesForMeetReturn.highestMaxNumberOfAthletesForMeet}) provided in the OME Meets section cannot be less than any Deck Pass Rule's Max Number of Athletes. Please delete any Deck Pass Rule in conflict with this validation rule in order to save changes made on this page.` :
          `The highest Max Number of Athletes For Meet (${issueWithMaxNumberOfAthletesForMeetReturn.highestMaxNumberOfAthletesForMeet}) cannot be less than any Deck Pass Rule's Max Number of Athletes. Please delete any Deck Pass Rule in conflict with this validation rule in order to save changes made on this page.`;
        setErrors({ ...errorState, error: error });
      }
      else if (Object.keys(errors).length === 0 && error === '') {
        setState({
          ...state, tryRedirect: true, submitButton: submitButton,
          omeMeetQualificationId: omeMeetQualificationId ? omeMeetQualificationId : Constants.DEFAULT_ID,
          qualificationBonusId: qualificationBonusId ? qualificationBonusId : Constants.DEFAULT_ID,
          omeMeetCoachPassId: omeMeetCoachPassId ? omeMeetCoachPassId : Constants.DEFAULT_ID
        });
      }
    }
  };

  const tryValidateBeforeShowingDeletePopUp = async (obj) => {
    const errors = await validate(formState) || {};
    setErrors(errors);

    //Don't show delete popup if maxEventsPerIndividualPerMeet has changed
    let bonusRuleError = false;
    if (omeMeetState?.objData?.omeMeetEligibility[0]?.maxEventsPerIndividualPerMeet === null && formState?.maxEventsPerIndividualPerMeet.toString() !== '') {
      bonusRuleError = true;
    } else if (ToIntIfInt(formState?.maxEventsPerIndividualPerMeet) !== omeMeetState?.objData?.omeMeetEligibility[0]?.maxEventsPerIndividualPerMeet
      && (formState?.maxEventsPerIndividualPerMeet !== '' || omeMeetState?.objData?.omeMeetEligibility[0]?.maxEventsPerIndividualPerMeet !== null)) {
      bonusRuleError = true;
    }

    if (bonusRuleError === true) {
      if (obj.omeMeetCoachPassId) {
        setErrors({ ...errorState, error: `Max Events Per Individual Per Meet (${formState.maxEventsPerIndividualPerMeet || 'blank'}) has changed. This field is used for Bonus Event Rule validation and cannot be updated when deleting a Deck Pass Rule. Please revert Max Events Per Individual Per Meet to its previous value (${omeMeetState?.objData?.omeMeetEligibility[0]?.maxEventsPerIndividualPerMeet !== null ? omeMeetState?.objData?.omeMeetEligibility[0]?.maxEventsPerIndividualPerMeet : 'blank'}) in order to delete a Deck Pass Rule.` });
      }
      else {
        setErrors({ ...errorState, error: `Max Events Per Individual Per Meet (${formState.maxEventsPerIndividualPerMeet || 'blank'}) has changed. This field is used for Bonus Event Rule validation and cannot be updated when deleting a Bonus Event Rule. Please revert Max Events Per Individual Per Meet to its previous value (${omeMeetState?.objData?.omeMeetEligibility[0]?.maxEventsPerIndividualPerMeet !== null ? omeMeetState?.objData?.omeMeetEligibility[0]?.maxEventsPerIndividualPerMeet : 'blank'}) in order to delete a Bonus Event Rule.` });
      }
    }

    //Show popup if there are no form validation errors and maxEventsPerIndividualPerMeet has not changed
    if (Object.keys(errors).length === 0 && bonusRuleError === false) {
      setModalState({
        ...modalState,
        displayPopUp: true,
        modalTitle: obj?.omeMeetCoachPassId ? 'Delete Deck Pass Rule?' : 'Delete Bonus Event Rule?',
        minQualifyingEvents: obj?.minQualifyingEvents >= 0 ? obj?.minQualifyingEvents : '',
        maxQualifyingEvents: obj?.maxQualifyingEvents >= 0 ? obj?.maxQualifyingEvents : '',
        bonusStandardCount: obj?.bonusStandardCount >= 0 ? obj?.bonusStandardCount : '',
        omeMeetQualificationId: obj?.omeMeetQualificationId || Constants.DEFAULT_ID,
        qualificationBonusId: obj?.qualificationBonusId || Constants.DEFAULT_ID,
        minSwimmers: obj?.minSwimmers >= 0 ? obj?.minSwimmers : '',
        maxSwimmers: obj?.maxSwimmers >= 0 ? obj?.maxSwimmers : '',
        coachPasses: obj?.coachPasses >= 0 ? obj?.coachPasses : '',
        omeMeetCoachPassId: obj?.omeMeetCoachPassId || Constants.DEFAULT_ID
      });
    }
  };

  const onNextButtonClicked = (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    if (isDirty === false && (formState.bonusEvents === false ||
      (formState.bonusEvents === true && omeMeetState.objData?.omeMeetQualification[0]?.omeQualificationBonus.length > 0))
      && omeMeetState.objData?.omeMeetCoachPass.length > 0) {
      tryValidateBeforeRedirect(HostRegistrationConstants.CONTINUE);
    }
    else {
      setIsDirty(true);
      handleSubmit();
      setState({ ...state, submitButton: HostRegistrationConstants.CONTINUE });
    }
  };

  const onSaveAndExitButtonClicked = (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    if (isDirty === false && (formState.bonusEvents === false ||
      (formState.bonusEvents === true && omeMeetState.objData?.omeMeetQualification[0]?.omeQualificationBonus.length > 0))
      && omeMeetState.objData?.omeMeetCoachPass.length > 0) {
      tryValidateBeforeRedirect(HostRegistrationConstants.SAVE_AND_EXIT);
    }
    else {
      setIsDirty(true);
      handleSubmit();
      setState({ ...state, submitButton: HostRegistrationConstants.SAVE_AND_EXIT });
    }
  };

  const onBackButtonClicked = (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    if (isDirty === false && (formState.bonusEvents === false ||
      (formState.bonusEvents === true && omeMeetState.objData?.omeMeetQualification[0]?.omeQualificationBonus.length > 0))
      && omeMeetState.objData?.omeMeetCoachPass.length > 0) {
      tryValidateBeforeRedirect(HostRegistrationConstants.BACK);
    }
    else {
      setIsDirty(true);
      handleSubmit();
      setState({ ...state, submitButton: HostRegistrationConstants.BACK });
    }
  };

  const onAddCoachPassRuleClicked = (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    tryValidateBeforeRedirect(HostRegistrationConstants.ADD + 'Coach');
  };


  const onEditCoachPassRuleClicked = (e, omeMeetCoachPassId) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    tryValidateBeforeRedirect(HostRegistrationConstants.EDIT + 'Coach', null, null, omeMeetCoachPassId);
  }

  const onDeleteCoachPassRuleClicked = (e, coach) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    tryValidateBeforeShowingDeletePopUp(coach);
  };

  const onDeleteCoachPassRule = (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    onModalCanceled();

    let updatedOmeMeet = JSON.parse(JSON.stringify(omeMeetState.objData));
    const editOmeMeetCopyReturn = editOmeMeetCopy(updatedOmeMeet);
    if (isDirty === true) {
      updatedOmeMeet = editOmeMeetCopyReturn?.updatedOmeMeet;
    }

    const selectedOmeMeetCoachPassIndex = updatedOmeMeet.omeMeetCoachPass.findIndex(x =>
      x.omeMeetCoachPassId === modalState.omeMeetCoachPassId);

    if (selectedOmeMeetCoachPassIndex > -1) {
      updatedOmeMeet.omeMeetCoachPass?.splice(selectedOmeMeetCoachPassIndex, 1);
      updatedOmeMeet.meet = undefined;
      putOmeMeet(omeMeetState?.objData?.omeMeetId, updatedOmeMeet);

      if (editOmeMeetCopyReturn?.tryPutOmeMeetEntry === true) {
        setState({ ...state, tryPutOmeMeetEntry: true, putOmeMeetEntryArray: editOmeMeetCopyReturn?.updatedOmeMeetEntry?.omeMeetEntry, doNotTryRedirect: true });
      }
    }
  };

  const onAddBonusEventRuleClicked = (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    tryValidateBeforeRedirect(HostRegistrationConstants.ADD + 'Bonus');
  }

  const onEditBonusEventRuleClicked = (e, omeMeetQualificationId, qualificationBonusId) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    tryValidateBeforeRedirect(HostRegistrationConstants.EDIT + 'Bonus', omeMeetQualificationId, qualificationBonusId, null);
  }

  const onDeleteBonusEventRuleClicked = (e, bonus) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    tryValidateBeforeShowingDeletePopUp(bonus);
  };

  const onDeleteBonusEventRule = (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    onModalCanceled();

    let updatedOmeMeet = JSON.parse(JSON.stringify(omeMeetState.objData));
    const editOmeMeetCopyReturn = editOmeMeetCopy(updatedOmeMeet);
    if (isDirty === true) {
      updatedOmeMeet = editOmeMeetCopyReturn?.updatedOmeMeet;
    }

    const selectedOmeMeetQualificationIndex = updatedOmeMeet.omeMeetQualification.findIndex(x =>
      x.omeMeetQualificationId === modalState.omeMeetQualificationId);

    if (selectedOmeMeetQualificationIndex > -1) {
      const selectedOmeQualificationBonusIndex = updatedOmeMeet.omeMeetQualification[selectedOmeMeetQualificationIndex]?.omeQualificationBonus.findIndex(x =>
        x.qualificationBonusId === modalState.qualificationBonusId);

      if (selectedOmeQualificationBonusIndex > -1) {
        updatedOmeMeet.omeMeetQualification[selectedOmeMeetQualificationIndex]?.omeQualificationBonus.splice(selectedOmeQualificationBonusIndex, 1);
        updatedOmeMeet.meet = undefined;
        putOmeMeet(omeMeetState?.objData?.omeMeetId, updatedOmeMeet);

        if (editOmeMeetCopyReturn?.tryPutOmeMeetEntry === true) {
          setState({ ...state, tryPutOmeMeetEntry: true, putOmeMeetEntryArray: editOmeMeetCopyReturn?.updatedOmeMeetEntry?.omeMeetEntry, doNotTryRedirect: true });
        }
      }
    }
  };

  const onModalCanceled = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    setModalState(INITIAL_MODAL_STATE);
  };

  const onSaveAndDelete = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    const omeMeetCopy = JSON.parse(JSON.stringify(omeMeetState.objData));
    const editOmeMeetCopyReturn = editOmeMeetCopy(omeMeetCopy);
    const updatedOmeMeet = editOmeMeetCopyReturn?.updatedOmeMeet;
    updatedOmeMeet.meet = undefined;
    putOmeMeet(omeMeetState?.objData?.omeMeetId, updatedOmeMeet);
    setState({ ...state, tryRedirect: true });
    if (editOmeMeetCopyReturn?.tryPutOmeMeetEntry === true) {
      setState({ ...state, tryPutOmeMeetEntry: true, putOmeMeetEntryArray: editOmeMeetCopyReturn?.updatedOmeMeetEntry?.omeMeetEntry });
    }
    else {
      setState({ ...state, tryRedirect: true });
    }
    setWarningModalState(INITIAL_WARNING_MODAL_STATE);
  };

  const onWarningModalCanceled = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setIsDirty(true);
    setWarningModalState(INITIAL_WARNING_MODAL_STATE);
  };

  const editOmeMeetCopy = (updatedOmeMeet) => {
    //Add 0,0,0 bonus event rule if bonus events are allowed for the meet,
    //and the omeMeetQualification is created
    if (formState.bonusEvents === true && updatedOmeMeet.omeMeetQualification?.length > 0) {
      if (updatedOmeMeet.omeMeetQualification[0].omeQualificationBonus.length === 0)
        updatedOmeMeet.omeMeetQualification[0].omeQualificationBonus = [{
          omeMeetQualificationId: updatedOmeMeet.omeMeetQualification[0].omeMeetQualificationId,
          minQualifyingEvents: 0,
          maxQualifyingEvents: 0,
          bonusStandardCount: 0
        }];
    }

    //Add 0,0,0 bonus event rule if bonus events are allowed 
    //for the meet and the omeMeetQualification is not created yet
    if (updatedOmeMeet.omeMeetQualification.length === 0) {
      updatedOmeMeet.omeMeetQualification = [{
        omeMeetId: updatedOmeMeet.omeMeetId,
        hasTimeStandards: null,
        hasNoSlowerThanTimeStandards: null,
        hasNoFasterThanTimeStandards: null,
        hasAthleteExceedsTimeStandardsRule: null,
        hasBonusTimeStandards: null,
        omeQualificationBonus: formState.bonusEvents === true ? [{
          minQualifyingEvents: formState.minQualifyingEvents,
          maxQualifyingEvents: formState.maxQualifyingEvents,
          bonusStandardCount: formState.bonusStandardCount
        }] : []
      }];
    }

    //Add 0,0,0 deck pass rule, if there are no deck passes provided
    if (updatedOmeMeet.omeMeetCoachPass?.length === 0) {
      updatedOmeMeet.omeMeetCoachPass = [{
        omeMeetId: omeMeetState.objData.omeMeetId,
        minSwimmers: 0,
        maxSwimmers: 0,
        coachPasses: 0
      }];
    }

    //Delete any bonus event rules if bonus events are not allowed for the meet
    if (formState.bonusEvents === false && updatedOmeMeet.omeMeetQualification?.length > 0) {
      if (updatedOmeMeet.omeMeetQualification[0].omeQualificationBonus.length > 0)
        updatedOmeMeet.omeMeetQualification[0].omeQualificationBonus = [];
    }

    let tryPutOmeMeetEntry = false;
    const updatedOmeMeetEntry = JSON.parse(JSON.stringify(omeMeetEntryState.objData));

    //Delete the Cost Per Athlete Individual Bonus Event Entry record if bonus rules are not allowed for the meet
    if (formState.bonusEvents === false && updatedOmeMeetEntry.omeMeetEntry?.length > 0) {
      for (let i = 0; i < updatedOmeMeetEntry.omeMeetEntry.length; i++) {
        if (updatedOmeMeetEntry.omeMeetEntry[i].omeMeetPrice?.length > 0) {
          const omeFeeTypeIdIndex = updatedOmeMeetEntry.omeMeetEntry[i].omeMeetPrice?.findIndex(x => x.omeFeeTypeId === ATHLETE_INDIVIDUAL_BONUS_EVENT_ENTRY_OME_FEE_TYPE_ID);
          if (omeFeeTypeIdIndex > -1) {
            updatedOmeMeetEntry.omeMeetEntry[i].omeMeetPrice.splice(omeFeeTypeIdIndex, 1);
            tryPutOmeMeetEntry = true;
          }
        }
      }
    }

    //Delete the Cost Per Relay Only Swimmer & Cost Per Relay Event Entry records if relay events are not allowed for the meet
    if (formState.relayEvents === false && updatedOmeMeetEntry.omeMeetEntry?.length > 0) {
      for (let i = 0; i < updatedOmeMeetEntry.omeMeetEntry.length; i++) {
        if (updatedOmeMeetEntry.omeMeetEntry[i].omeMeetPrice?.length > 0) {
          const relayOnlySwimmerOmeFeeTypeIdIndex = updatedOmeMeetEntry.omeMeetEntry[i].omeMeetPrice?.findIndex(x => x.omeFeeTypeId === RELAY_ONLY_SWIMMER_OME_FEE_TYPE_ID);
          if (relayOnlySwimmerOmeFeeTypeIdIndex > -1) {
            updatedOmeMeetEntry.omeMeetEntry[i].omeMeetPrice.splice(relayOnlySwimmerOmeFeeTypeIdIndex, 1);
            tryPutOmeMeetEntry = true;
          }
          const relayEventEntryOmeFeeTypeIdIndex = updatedOmeMeetEntry.omeMeetEntry[i].omeMeetPrice?.findIndex(x => x.omeFeeTypeId === RELAY_EVENT_ENTRY_OME_FEE_TYPE_ID);
          if (relayEventEntryOmeFeeTypeIdIndex > -1) {
            updatedOmeMeetEntry.omeMeetEntry[i].omeMeetPrice.splice(relayEventEntryOmeFeeTypeIdIndex, 1);
            tryPutOmeMeetEntry = true;
          }
        }
        //Handle includeRelayOnlySwimmersInMaxNumber if relayEvents is set to false
        if (updatedOmeMeetEntry.omeMeetEntry[i].includeRelayOnlySwimmersInMaxNumber !== null) {
          updatedOmeMeetEntry.omeMeetEntry[i].includeRelayOnlySwimmersInMaxNumber = null;
          tryPutOmeMeetEntry = true;
        }
      }
    }

    //Delete the Cost Per Relay Only Swimmer if relay events are allowed for the meet but Relay Only Swimmers are not allowed
    if (formState.allowRelayOnlySwimmer === false && updatedOmeMeetEntry.omeMeetEntry?.length > 0) {
      for (let i = 0; i < updatedOmeMeetEntry.omeMeetEntry.length; i++) {
        if (updatedOmeMeetEntry.omeMeetEntry[i].omeMeetPrice?.length > 0) {
          const relayOnlySwimmerOmeFeeTypeIdIndex = updatedOmeMeetEntry.omeMeetEntry[i].omeMeetPrice?.findIndex(x => x.omeFeeTypeId === RELAY_ONLY_SWIMMER_OME_FEE_TYPE_ID);
          if (relayOnlySwimmerOmeFeeTypeIdIndex > -1) {
            updatedOmeMeetEntry.omeMeetEntry[i].omeMeetPrice.splice(relayOnlySwimmerOmeFeeTypeIdIndex, 1);
            tryPutOmeMeetEntry = true;
          }
        }
        //Handle includeRelayOnlySwimmersInMaxNumber if allowRelayOnlySwimmer is set to false
        if (updatedOmeMeetEntry.omeMeetEntry[i].includeRelayOnlySwimmersInMaxNumber !== null) {
          updatedOmeMeetEntry.omeMeetEntry[i].includeRelayOnlySwimmersInMaxNumber = null;
          tryPutOmeMeetEntry = true;
        }
      }
    }

    //Any relay event related info is cleared out below if relayEvents is set to false
    updatedOmeMeet.omeMeetEligibility[0] = {
      ...updatedOmeMeet.omeMeetEligibility[0],
      omeMeetId: updatedOmeMeet.omeMeetId,
      allowRelayOnlySwimmer: formState.relayEvents === true ? (formState.allowRelayOnlySwimmer ?? null) : null,
      relayTeamFromRosterOnly: formState.relayEvents === true ? (formState.relayTeamFromRosterOnly ?? null) : null,
      forceAggregateIfFasterThanTeamTime: formState.relayEvents === true ? (formState.forceAggregateIfFasterThanTeamTime ?? null) : null,
      maxEventsPerIndividualPerMeet: formState.maxEventsPerIndividualPerMeet || null,
      maxEventsPerIndividualPerSession: formState.maxEventsPerIndividualPerSession || null,
      relayEvents: formState.relayEvents,
      maxRelayTeamEntriesPerEvent: formState.relayEvents === true ? (formState.maxRelayTeamEntriesPerEvent || null) : null,
      maxRelayEventsPerIndividualPerMeet: formState.relayEvents === true ? (formState.maxRelayEventsPerIndividualPerMeet || null) : null,
      bonusEvents: formState.bonusEvents,
      exclude50FromBonusTotal: HIDE_OME_ELIGIBILITY_50_BONUS_OPTION !== 1 ? (formState.bonusEvents === true ? (formState.exclude50FromBonusTotal ?? null) : null) : null,
      usCitizenOnly: updatedOmeMeet.omeMeetEligibility[0]?.usCitizenOnly ?? null,
      allowRepOtherCountry: updatedOmeMeet.omeMeetEligibility[0]?.allowRepOtherCountry ?? null,
      useAdjustedSwimTime: formState.useAdjustedSwimTime
    }

    updatedOmeMeet.includeRelaySwimmerForCoachPasses = formState.relayEvents === true && formState.allowRelayOnlySwimmer === true ? (formState.includeRelaySwimmerForCoachPasses ?? null) : null;
    updatedOmeMeet.credentialsCanBeAddedAfterCap = formState.credentialsCanBeAddedAfterCap ?? null;
    updatedOmeMeet.credentialsAfterCapFeeAmount = formState.credentialsCanBeAddedAfterCap === true && formState.credentialsAfterCapFeeAmount !== '' ? formState.credentialsAfterCapFeeAmount : null;
    updatedOmeMeet.meet = undefined;

    //set has hasAthleteExceedsTimeStandardsRelayRule to null if relay events are not allowed
    if (updatedOmeMeet.omeMeetQualification[0] && formState.relayEvents === false) {
      updatedOmeMeet.omeMeetQualification[0] = {
        ...updatedOmeMeet.omeMeetQualification[0],
        hasAthleteExceedsTimeStandardsRelayRule: null
      }
    }

    //set has bonusTimeStandards to null if bonus events are not allowed
    if (updatedOmeMeet.omeMeetQualification[0] && formState.bonusEvents === false) {
      updatedOmeMeet.omeMeetQualification[0] = {
        ...updatedOmeMeet.omeMeetQualification[0],
        hasBonusTimeStandards: null
      }
    }

    //Remove any bonus time standards if the meet does not have bonus events
    if (formState.bonusEvents === false && updatedOmeMeet.meetAgeGroup.length > 0) {
      for (let i = 0; i < updatedOmeMeet.meetAgeGroup.length; i++) {
        if (updatedOmeMeet.meetAgeGroup[i].meetAgeGroupTimeStandard.length > 0) {
          for (let j = 0; j < updatedOmeMeet.meetAgeGroup[i].meetAgeGroupTimeStandard.length; j++) {
            updatedOmeMeet.meetAgeGroup[i].meetAgeGroupTimeStandard[j] = {
              ...updatedOmeMeet.meetAgeGroup[i].meetAgeGroupTimeStandard[j],
              bonusTimeStandardAgeGroupId: null,
              bonusTimeStandardAgeGroup: undefined
            }
          }
        }
      }
    }

    //Remove any bonus time standard cuts if the meet does not allow bonus events
    if (formState.bonusEvents === false && updatedOmeMeet.meetAgeGroup.length > 0) {
      for (let i = 0; i < updatedOmeMeet.meetAgeGroup.length; i++) {
        if (updatedOmeMeet.meetAgeGroup[i].meetEvent.length > 0) {
          for (let j = 0; j < updatedOmeMeet.meetAgeGroup[i].meetEvent.length; j++) {
            updatedOmeMeet.meetAgeGroup[i].meetEvent[j].meetEventBonusTime = [];
          }
        }
      }
    }

    return {
      updatedOmeMeet,
      updatedOmeMeetEntry,
      tryPutOmeMeetEntry
    };
  }

  function submitFormCallback(formState) {
    //Prevent user from setting relayEvents to false if they have already added relay events
    let relayError = false;
    if (formState.relayEvents === false) {
      if (omeMeetState.objData.meetAgeGroup?.length > 0) {
        for (let i = 0; i < omeMeetState.objData.meetAgeGroup.length; i++) {
          for (let j = 0; j < omeMeetState.objData.meetAgeGroup[i].meetEvent.length; j++) {
            if (omeMeetState.objData.meetAgeGroup[i].meetEvent[j]?.event?.isRelay === true) {
              relayError = true;
            }
          }
        }
      }
    }
    if (relayError === true) {
      setErrors({ ...errorState, error: `Meet has Relay Events? cannot be set to 'No' because relay events have been added on the Events page. To be permitted to set this field to 'No', navigate to the Events page and delete all relay events.` });
    }
    else if (relayError === false) {
      //Check to see if any bonus event rules conflict with the max number of events per individual per meet
      let error = ''
      if (issueWithMaxEventsPerIndividualPerMeet() === true) {
        error = `Max Events Per Individual Per Meet (${formState.maxEventsPerIndividualPerMeet}) cannot be less than the sum of any Bonus Event Rule's Max Number of Qualifying Events and Allowed Number of Bonus Events. Please delete any Bonus Event Rule in conflict with this validation rule in order to save changes made on this page.`
        setErrors({ ...errorState, error: error });
      }
      //Check to see if any deck pass rules conflict with the max number of athletes for meet
      const issueWithMaxNumberOfAthletesForMeetReturn = issueWithMaxNumberOfAthletesForMeet();
      if (issueWithMaxNumberOfAthletesForMeetReturn?.issue === true) {
        error = error ? error + `\n\n The highest Max Number of Athletes For Meet (${issueWithMaxNumberOfAthletesForMeetReturn.highestMaxNumberOfAthletesForMeet}) provided in the OME Meets section cannot be less than any Deck Pass Rule's Max Number of Athletes. Please delete any Deck Pass Rule in conflict with this validation rule in order to save changes made on this page.` :
          `The highest Max Number of Athletes For Meet (${issueWithMaxNumberOfAthletesForMeetReturn.highestMaxNumberOfAthletesForMeet}) cannot be less than any Deck Pass Rule's Max Number of Athletes. Please delete any Deck Pass Rule in conflict with this validation rule in order to save changes made on this page.`;
        setErrors({ ...errorState, error: error });
      }
      else if (error === '') {
        //Check to see if any relay / bonus time Standard / event changes need to be handled
        if (showDeleteWarningMessage(formState) === false) {
          const updatedOmeMeet = JSON.parse(JSON.stringify(omeMeetState.objData));
          updatedOmeMeet.meet = undefined;
          const editOmeMeetCopyReturn = editOmeMeetCopy(updatedOmeMeet);
          putOmeMeet(omeMeetState?.objData?.omeMeetId, editOmeMeetCopyReturn?.updatedOmeMeet);
          if (editOmeMeetCopyReturn?.tryPutOmeMeetEntry === true) {
            setState({ ...state, tryPutOmeMeetEntry: true, putOmeMeetEntryArray: editOmeMeetCopyReturn?.updatedOmeMeetEntry?.omeMeetEntry });
          }
          else {
            setState({ ...state, tryRedirect: true });
          }
        }
      }
    }
  }

  function formStateHasBeenSaved(eligibilityFormState) {
    //compare formState to omeMeetState to see if formState has been saved
    if (eligibilityFormState?.includeRelaySwimmerForCoachPasses !== omeMeetState?.objData?.includeRelaySwimmerForCoachPasses
      && (eligibilityFormState?.includeRelaySwimmerForCoachPasses !== '' || omeMeetState?.objData?.includeRelaySwimmerForCoachPasses !== null)) {
      return false;
    }
    if (eligibilityFormState?.relayEvents !== omeMeetState?.objData?.omeMeetEligibility[0]?.relayEvents
      && (eligibilityFormState?.relayEvents !== '' || omeMeetState?.objData?.omeMeetEligibility[0]?.relayEvents !== null)) {
      return false;
    }
    if (eligibilityFormState?.allowRelayOnlySwimmer !== omeMeetState?.objData?.omeMeetEligibility[0]?.allowRelayOnlySwimmer
      && (eligibilityFormState?.allowRelayOnlySwimmer !== '' || omeMeetState?.objData?.omeMeetEligibility[0]?.allowRelayOnlySwimmer !== null)) {
      return false;
    }
    if (eligibilityFormState?.relayTeamFromRosterOnly !== omeMeetState?.objData?.omeMeetEligibility[0]?.relayTeamFromRosterOnly
      && (eligibilityFormState?.relayTeamFromRosterOnly !== '' || omeMeetState?.objData?.omeMeetEligibility[0]?.relayTeamFromRosterOnly !== null)) {
      return false;
    }
    if (eligibilityFormState?.forceAggregateIfFasterThanTeamTime !== omeMeetState?.objData?.omeMeetEligibility[0]?.forceAggregateIfFasterThanTeamTime
      && (eligibilityFormState?.forceAggregateIfFasterThanTeamTime !== '' || omeMeetState?.objData?.omeMeetEligibility[0]?.forceAggregateIfFasterThanTeamTime !== null)) {
      return false;
    }
    if (eligibilityFormState?.bonusEvents !== omeMeetState?.objData?.omeMeetEligibility[0]?.bonusEvents
      && (eligibilityFormState?.bonusEvents !== '' || omeMeetState?.objData?.omeMeetEligibility[0]?.bonusEvents !== null)) {
      return false;
    }
    if (eligibilityFormState?.credentialsCanBeAddedAfterCap !== omeMeetState?.objData?.credentialsCanBeAddedAfterCap
      && (eligibilityFormState?.credentialsCanBeAddedAfterCap !== '' || omeMeetState?.objData?.credentialsCanBeAddedAfterCap !== null)) {
      return false;
    }
    if (eligibilityFormState?.credentialsCanBeAddedAfterCap === true &&
      (eligibilityFormState?.credentialsAfterCapFeeAmount !== omeMeetState?.objData?.credentialsAfterCapFeeAmount
        && (eligibilityFormState?.credentialsAfterCapFeeAmount !== '' || omeMeetState?.objData?.credentialsAfterCapFeeAmount !== null))) {
      return false;
    }
    if (eligibilityFormState?.maxEventsPerIndividualPerMeet !== omeMeetState?.objData?.omeMeetEligibility[0]?.maxEventsPerIndividualPerMeet
      && (eligibilityFormState?.maxEventsPerIndividualPerMeet !== '' || omeMeetState?.objData?.omeMeetEligibility[0]?.maxEventsPerIndividualPerMeet !== null)) {
      return false;
    }
    if (eligibilityFormState?.maxEventsPerIndividualPerSession !== omeMeetState?.objData?.omeMeetEligibility[0]?.maxEventsPerIndividualPerSession
      && (eligibilityFormState?.maxEventsPerIndividualPerSession !== '' || omeMeetState?.objData?.omeMeetEligibility[0]?.maxEventsPerIndividualPerSession !== null)) {
      return false;
    }
    if (eligibilityFormState?.maxRelayTeamEntriesPerEvent !== omeMeetState?.objData?.omeMeetEligibility[0]?.maxRelayTeamEntriesPerEvent
      && (eligibilityFormState?.maxRelayTeamEntriesPerEvent !== '' || omeMeetState?.objData?.omeMeetEligibility[0]?.maxRelayTeamEntriesPerEvent !== null)) {
      return false;
    }
    if (eligibilityFormState?.maxRelayEventsPerIndividualPerMeet !== omeMeetState?.objData?.omeMeetEligibility[0]?.maxRelayEventsPerIndividualPerMeet
      && (eligibilityFormState?.maxRelayEventsPerIndividualPerMeet !== '' || omeMeetState?.objData?.omeMeetEligibility[0]?.maxRelayEventsPerIndividualPerMeet !== null)) {
      return false;
    }
    if (eligibilityFormState?.useAdjustedSwimTime !== omeMeetState?.objData?.omeMeetEligibility[0]?.useAdjustedSwimTime
      && (eligibilityFormState?.useAdjustedSwimTime !== '' || omeMeetState?.objData?.omeMeetEligibility[0]?.useAdjustedSwimTime !== null)) {
      return false;
    }

    return true;
  }

  function showDeleteWarningMessage(formState) {
    let numMeetEvents = 0;
    let hasMeetEventBonusTime = false;
    let deleteAllEventBonusTimeStandardCuts = false;
    let timeStandardWarningMsg = '';

    for (let i = 0; i < omeMeetState?.objData?.meetAgeGroup.length; i++) {
      for (let j = 0; j < omeMeetState?.objData?.meetAgeGroup[i].meetEvent.length; j++) {
        numMeetEvents += 1;
        if (omeMeetState?.objData?.meetAgeGroup[i].meetEvent[j].meetEventBonusTime.length > 0) {
          hasMeetEventBonusTime = true;
        }
      }
    }

    //If the meet has bonus time standards, show time standard warning
    if ((numMeetEvents === 0 || (numMeetEvents > 0 && hasMeetEventBonusTime === false)) && omeMeetState.objData?.omeMeetQualification[0]?.hasBonusTimeStandards === true) {
      timeStandardWarningMsg = "Changing 'Meet allows Bonus Events?' from 'Yes' to 'No' will delete all Age Group Bonus Time Standards."
    }

    //If the meet has bonus time standards and bonus event cuts, show time standard and bonus event cut warning
    if (numMeetEvents > 0 && hasMeetEventBonusTime === true && omeMeetState.objData?.omeMeetQualification[0]?.hasBonusTimeStandards === true) {
      timeStandardWarningMsg = "Changing 'Meet allows Bonus Events?' from 'Yes' to 'No' will delete all Age Group Bonus Time Standards and Event Bonus Time Standard Cuts."
    }

    //compare formState to omeMeetState to see if changes will affect any events using time standard cuts
    if (formState?.bonusEvents === false && omeMeetState?.objData?.omeMeetEligibility[0]?.bonusEvents === true &&
      omeMeetState.objData?.omeMeetQualification[0]?.hasBonusTimeStandards === true) {
      deleteAllEventBonusTimeStandardCuts = true;
    }

    let deleteCostPerAthleteIndividualBonusEventEntry = false;
    let bonusPricingWarningMessage = '';

    if (formState.bonusEvents === false) {
      for (let i = 0; i < omeMeetEntryState?.objData?.omeMeetEntry?.length; i++) {
        for (let j = 0; j < omeMeetEntryState?.objData?.omeMeetEntry[i].omeMeetPrice?.length; j++) {
          if (omeMeetEntryState?.objData?.omeMeetEntry[i].omeMeetPrice[j].omeFeeTypeId === ATHLETE_INDIVIDUAL_BONUS_EVENT_ENTRY_OME_FEE_TYPE_ID) {
            deleteCostPerAthleteIndividualBonusEventEntry = true;
            bonusPricingWarningMessage = "Changing 'Meet allows Bonus Events?' from 'Yes' to 'No' will delete the 'Cost Per Athlete Individual Bonus Event Entry' provided on the OME Meets page."
          }
        }
      }
    }

    let deleteRelayRelatedCosts = false;
    let relayOnlySwimmerPricingWarningMessage = '';
    let relayEntryPricingWarningMessage = '';

    let deleteIncludeRelayOnlySwimmersInMaxNumber = false;
    let includeRelayOnlySwimmersInMaxNumberWarningMessage = '';

    if (formState.relayEvents === false) {
      for (let i = 0; i < omeMeetEntryState?.objData?.omeMeetEntry?.length; i++) {
        for (let j = 0; j < omeMeetEntryState?.objData?.omeMeetEntry[i].omeMeetPrice?.length; j++) {
          if (omeMeetEntryState?.objData?.omeMeetEntry[i].omeMeetPrice[j].omeFeeTypeId === RELAY_ONLY_SWIMMER_OME_FEE_TYPE_ID) {
            deleteRelayRelatedCosts = true;
            relayOnlySwimmerPricingWarningMessage = "Changing 'Meet has Relay Events?' from 'Yes' to 'No' will delete the 'Cost Per Relay Only Swimmer' provided on the OME Meets page."
          }
          if (omeMeetEntryState?.objData?.omeMeetEntry[i].omeMeetPrice[j].omeFeeTypeId === RELAY_EVENT_ENTRY_OME_FEE_TYPE_ID) {
            deleteRelayRelatedCosts = true;
            relayEntryPricingWarningMessage = "Changing 'Meet has Relay Events?' from 'Yes' to 'No' will delete the 'Cost Per Relay Event Entry' provided on the OME Meets page."
          }
        }
        if (omeMeetEntryState?.objData?.omeMeetEntry[i].includeRelayOnlySwimmersInMaxNumber !== null) {
          deleteIncludeRelayOnlySwimmersInMaxNumber = true;
          includeRelayOnlySwimmersInMaxNumberWarningMessage = "Changing 'Meet has Relay Events?' from 'Yes' to 'No' will delete the 'Include Relay Only Swimmers in Max Number of Athletes?' selection provided on the OME Meets page."
        }
      }
    }

    if (formState.allowRelayOnlySwimmer === false) {
      for (let i = 0; i < omeMeetEntryState?.objData?.omeMeetEntry?.length; i++) {
        for (let j = 0; j < omeMeetEntryState?.objData?.omeMeetEntry[i].omeMeetPrice?.length; j++) {
          if (omeMeetEntryState?.objData?.omeMeetEntry[i].omeMeetPrice[j].omeFeeTypeId === RELAY_ONLY_SWIMMER_OME_FEE_TYPE_ID) {
            deleteRelayRelatedCosts = true;
            relayOnlySwimmerPricingWarningMessage = "Changing 'Meet allows Relay Only Swimmers?' from 'Yes' to 'No' will delete the 'Cost Per Relay Only Swimmer' provided on the OME Meets page."
          }
        }
        if (omeMeetEntryState?.objData?.omeMeetEntry[i].includeRelayOnlySwimmersInMaxNumber !== null) {
          deleteIncludeRelayOnlySwimmersInMaxNumber = true;
          includeRelayOnlySwimmersInMaxNumberWarningMessage = "Changing 'Meet allows Relay Only Swimmers?' from 'Yes' to 'No' will delete the 'Include Relay Only Swimmers in Max Number of Athletes?' selection provided on the OME Meets page."
        }
      }
    }

    if (deleteAllEventBonusTimeStandardCuts === true || deleteCostPerAthleteIndividualBonusEventEntry === true ||
      deleteRelayRelatedCosts === true || deleteIncludeRelayOnlySwimmersInMaxNumber === true) {
      setWarningModalState({
        ...warningModalState,
        displayPopUp: true,
        deleteAllEventBonusTimeStandardCuts: deleteAllEventBonusTimeStandardCuts,
        timeStandardWarningMsg: timeStandardWarningMsg,
        deleteCostPerAthleteIndividualBonusEventEntry: deleteCostPerAthleteIndividualBonusEventEntry,
        bonusPricingWarningMessage: bonusPricingWarningMessage,
        deleteRelayRelatedCosts: deleteRelayRelatedCosts,
        relayOnlySwimmerPricingWarningMessage: relayOnlySwimmerPricingWarningMessage,
        relayEntryPricingWarningMessage: relayEntryPricingWarningMessage,
        deleteIncludeRelayOnlySwimmersInMaxNumber: deleteIncludeRelayOnlySwimmersInMaxNumber,
        includeRelayOnlySwimmersInMaxNumberWarningMessage: includeRelayOnlySwimmersInMaxNumberWarningMessage
      });
      return true;
    }

    else {
      //If the formState changes are not related to relay or bonus events, don't show warning message
      return false;
    }
  }

  function issueWithMaxEventsPerIndividualPerMeet() {
    if (formState.bonusEvents === false || formState.maxEventsPerIndividualPerMeet === '') {
      return false;
    }
    else if (omeMeetState.objData.omeMeetQualification[0] !== undefined &&
      omeMeetState.objData.omeMeetQualification[0]?.omeQualificationBonus?.length > 0) {
      for (let i = 0; i < omeMeetState.objData.omeMeetQualification[0]?.omeQualificationBonus?.length; i++) {
        let maxQualifyingEventsNum = parseInt(omeMeetState.objData.omeMeetQualification[0]?.omeQualificationBonus[i].maxQualifyingEvents);
        let bonusStandardCountNum = parseInt(omeMeetState.objData.omeMeetQualification[0]?.omeQualificationBonus[i].bonusStandardCount);
        if ((maxQualifyingEventsNum + bonusStandardCountNum) > parseInt(formState.maxEventsPerIndividualPerMeet)) {
          return true;
        }
      }
    }

    return false;
  }

  function issueWithMaxNumberOfAthletesForMeet() {
    const maxNumberOfAthletesForMeetArray = omeMeetEntryState?.objData?.omeMeetEntry?.map((x) => x.maxNumberOfAthletesForMeet)?.filter((x) => x !== null);
    const highestMaxNumberOfAthletesForMeet = maxNumberOfAthletesForMeetArray?.length > 0 ? Math.max(...maxNumberOfAthletesForMeetArray) : undefined

    if (omeMeetState.objData.omeMeetCoachPass !== undefined &&
      omeMeetState.objData.omeMeetCoachPass?.length > 0 &&
      highestMaxNumberOfAthletesForMeet !== undefined) {
      for (let i = 0; i < omeMeetState.objData.omeMeetCoachPass?.length; i++) {
        let maxSwimmersNum = parseInt(omeMeetState.objData.omeMeetCoachPass[i].maxSwimmers);
        if (maxSwimmersNum > highestMaxNumberOfAthletesForMeet) {
          return {
            issue: true,
            highestMaxNumberOfAthletesForMeet: highestMaxNumberOfAthletesForMeet
          };
        }
      }
    }

    return {
      issue: false,
      highestMaxNumberOfAthletesForMeet: highestMaxNumberOfAthletesForMeet
    };
  }

  useEffect(() => {
    if (Object.keys(omeMeetState.objData).length > 0 && environmentVariableState.isLoaded === true) {
      //Sort grid data to display in ascending order
      let omeQualificationBonusCopy = [];
      if (omeMeetState.objData.omeMeetQualification[0]) {
        omeQualificationBonusCopy = JSON.parse(JSON.stringify(omeMeetState.objData.omeMeetQualification[0]?.omeQualificationBonus));
        omeQualificationBonusCopy.sort(function (a, b) { return a.minQualifyingEvents - b.minQualifyingEvents });
      }
      let omeCoachPassCopy = [];
      if (omeMeetState.objData.omeMeetCoachPass) {
        omeCoachPassCopy = JSON.parse(JSON.stringify(omeMeetState.objData.omeMeetCoachPass));
        omeCoachPassCopy.sort(function (a, b) { return a.minSwimmers - b.minSwimmers });
      }
      setState({
        ...state,
        sortedBonusGridData: omeQualificationBonusCopy,
        sortedCoachGridData: omeCoachPassCopy
      });
      //Set formState from location.state if 'Back' button was clicked on bonus event detail page
      if (location.state && location.state?.eligibilityFormState) {
        setFormData({
          ...formState,
          includeRelaySwimmerForCoachPasses: location.state?.eligibilityFormState?.includeRelaySwimmerForCoachPasses ?? '',
          maxEventsPerIndividualPerMeet: location.state?.eligibilityFormState?.maxEventsPerIndividualPerMeet !== null &&
            location.state?.eligibilityFormState?.maxEventsPerIndividualPerMeet !== undefined ?
            location.state?.eligibilityFormState?.maxEventsPerIndividualPerMeet : '',
          maxEventsPerIndividualPerSession: location.state?.eligibilityFormState?.maxEventsPerIndividualPerSession !== null &&
            location.state?.eligibilityFormState?.maxEventsPerIndividualPerSession !== undefined ?
            location.state?.eligibilityFormState?.maxEventsPerIndividualPerSession : '',
          relayEvents: location.state?.eligibilityFormState?.relayEvents ?? '',
          allowRelayOnlySwimmer: location.state?.eligibilityFormState?.allowRelayOnlySwimmer ?? '',
          relayTeamFromRosterOnly: location.state?.eligibilityFormState?.relayTeamFromRosterOnly ?? '',
          forceAggregateIfFasterThanTeamTime: location.state?.eligibilityFormState?.forceAggregateIfFasterThanTeamTime ?? '',
          maxRelayTeamEntriesPerEvent: location.state?.eligibilityFormState?.maxRelayTeamEntriesPerEvent !== null &&
            location.state?.eligibilityFormState?.maxRelayTeamEntriesPerEvent !== undefined ?
            location.state?.eligibilityFormState?.maxRelayTeamEntriesPerEvent : '',
          maxRelayEventsPerIndividualPerMeet: location.state?.eligibilityFormState?.maxRelayEventsPerIndividualPerMeet !== null &&
            location.state?.eligibilityFormState?.maxRelayEventsPerIndividualPerMeet !== undefined ?
            location.state?.eligibilityFormState?.maxRelayEventsPerIndividualPerMeet : '',
          bonusEvents: location.state?.eligibilityFormState?.bonusEvents ?? '',
          exclude50FromBonusTotal: location.state?.eligibilityFormState?.HIDE_OME_ELIGIBILITY_50_BONUS_OPTION !== 1 ? location.state?.eligibilityFormState?.exclude50FromBonusTotal ?? '' : '',
          credentialsCanBeAddedAfterCap: location.state?.credentialsCanBeAddedAfterCap ?? '',
          credentialsAfterCapFeeAmount: location.state?.eligibilityFormState?.credentialsAfterCapFeeAmount !== null && location.state?.eligibilityFormState?.credentialsAfterCapFeeAmount !== undefined ?
            location.state?.eligibilityFormState?.credentialsAfterCapFeeAmount : '',
          useAdjustedSwimTime: location.state?.eligibilityFormState?.useAdjustedSwimTime ?? '',
          MAX_RELAY_TEAM_ENTRIES_PER_EVENT: MAX_RELAY_TEAM_ENTRIES_PER_EVENT,
          HIDE_OME_ELIGIBILITY_50_BONUS_OPTION: HIDE_OME_ELIGIBILITY_50_BONUS_OPTION
        });
        if (formStateHasBeenSaved(location.state?.eligibilityFormState) === false) {
          setIsDirty(true);
        }
      }
      else {
        //Set formState from omeMeetState
        setFormData({
          ...formState,
          includeRelaySwimmerForCoachPasses: omeMeetState?.objData?.includeRelaySwimmerForCoachPasses ?? '',
          maxEventsPerIndividualPerMeet: omeMeetState?.objData?.omeMeetEligibility[0]?.maxEventsPerIndividualPerMeet !== null &&
            omeMeetState?.objData?.omeMeetEligibility[0]?.maxEventsPerIndividualPerMeet !== undefined ?
            omeMeetState?.objData?.omeMeetEligibility[0]?.maxEventsPerIndividualPerMeet : '',
          maxEventsPerIndividualPerSession: omeMeetState?.objData?.omeMeetEligibility[0]?.maxEventsPerIndividualPerSession !== null &&
            omeMeetState?.objData?.omeMeetEligibility[0]?.maxEventsPerIndividualPerSession !== undefined ?
            omeMeetState?.objData?.omeMeetEligibility[0]?.maxEventsPerIndividualPerSession : '',
          relayEvents: omeMeetState?.objData?.omeMeetEligibility[0]?.relayEvents ?? '',
          allowRelayOnlySwimmer: omeMeetState?.objData.omeMeetEligibility[0]?.allowRelayOnlySwimmer ?? '',
          relayTeamFromRosterOnly: omeMeetState?.objData.omeMeetEligibility[0]?.relayTeamFromRosterOnly ?? '',
          forceAggregateIfFasterThanTeamTime: omeMeetState?.objData.omeMeetEligibility[0]?.forceAggregateIfFasterThanTeamTime ?? '',
          maxRelayTeamEntriesPerEvent: omeMeetState?.objData?.omeMeetEligibility[0]?.maxRelayTeamEntriesPerEvent !== null &&
            omeMeetState?.objData?.omeMeetEligibility[0]?.maxRelayTeamEntriesPerEvent !== undefined ?
            omeMeetState?.objData?.omeMeetEligibility[0]?.maxRelayTeamEntriesPerEvent : '',
          maxRelayEventsPerIndividualPerMeet: omeMeetState?.objData?.omeMeetEligibility[0]?.maxRelayEventsPerIndividualPerMeet !== null &&
            omeMeetState?.objData?.omeMeetEligibility[0]?.maxRelayEventsPerIndividualPerMeet !== undefined ?
            omeMeetState?.objData?.omeMeetEligibility[0]?.maxRelayEventsPerIndividualPerMeet : '',
          bonusEvents: omeMeetState?.objData?.omeMeetEligibility[0]?.bonusEvents ?? '',
          exclude50FromBonusTotal: HIDE_OME_ELIGIBILITY_50_BONUS_OPTION !== 1 ? omeMeetState?.objData?.omeMeetEligibility[0]?.exclude50FromBonusTotal ?? '' : '',
          credentialsCanBeAddedAfterCap: omeMeetState?.objData?.credentialsCanBeAddedAfterCap ?? '',
          credentialsAfterCapFeeAmount: omeMeetState?.objData?.credentialsAfterCapFeeAmount !== null && omeMeetState?.objData?.credentialsAfterCapFeeAmount !== undefined ?
            omeMeetState?.objData?.credentialsAfterCapFeeAmount : '',
          useAdjustedSwimTime: omeMeetState?.objData?.omeMeetEligibility[0]?.useAdjustedSwimTime ?? '',
          MAX_RELAY_TEAM_ENTRIES_PER_EVENT: MAX_RELAY_TEAM_ENTRIES_PER_EVENT,
          HIDE_OME_ELIGIBILITY_50_BONUS_OPTION: HIDE_OME_ELIGIBILITY_50_BONUS_OPTION
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [omeMeetState.objData, omeMeetEntryState.objData, environmentVariableState]);

  useEffect(() => {
    if (Object.keys(omeMeetState.objData).length > 0) {
      const isSaved = formStateHasBeenSaved(formState);
      setLeftNavModalState({ ...leftNavModalState, formStateSaved: isSaved });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState, omeMeetState.objData]);

  useEffect(() => {
    if (omeMeetState.isSaving === false && omeMeetEntryState.isSaving === false &&
      isSubmitting === false && state.tryPutOmeMeetEntry === true) {
      putOmeMeetEntry(omeMeetState?.objData?.omeMeetId, {
        omeMeetId: omeMeetState?.objData?.omeMeetId,
        omeMeetEntry: state.putOmeMeetEntryArray
      });
      if (state.doNotTryRedirect === true) {
        setState({ ...state, tryPutOmeMeetEntry: false, putOmeMeetEntryArray: [], doNotTryRedirect: false, tryRedirect: false });
      }
      else {
        setState({ ...state, tryPutOmeMeetEntry: false, putOmeMeetEntryArray: [], doNotTryRedirect: false, tryRedirect: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting, omeMeetState.isSaving, omeMeetEntryState.isSaving, state])

  useEffect(() => {
    if (omeMeetState.isSaving === false && omeMeetEntryState.isSaving === false &&
      isSubmitting === false && state.tryRedirect === true) {
      if (state.submitButton === HostRegistrationConstants.CONTINUE) {
        let error = '';
        if (omeMeetState?.objData?.omeMeetCoachPass?.length === 0) {
          error = 'At least one Deck Pass Rule is required';
        }
        if (omeMeetState?.objData?.omeMeetEligibility[0]?.bonusEvents === true && (omeMeetState?.objData?.omeMeetQualification[0] === undefined ||
          omeMeetState?.objData?.omeMeetQualification[0]?.omeQualificationBonus?.length === 0)) {
          error = error ? error + '\n At least one Bonus Event Rule is required' : 'At least one Bonus Event Rule is required';
        }
        if (error !== '') {
          setErrors({ ...errorState, error: error });
        }
        else {
          if (omeMeetState.route !== '') {
            navigate(NavLinks.MEET_HOST_RESTRICTIONS);
          }
          else {
            navigate(HostNavLinks.OME_MEET_RESTRICTIONS);
          }
        }
      }
      else if (state.submitButton === HostRegistrationConstants.SAVE_AND_EXIT) {
        if (omeMeetState?.objData?.omeMeetEligibility[0]?.bonusEvents === true && (omeMeetState?.objData?.omeMeetQualification[0] === undefined ||
          omeMeetState?.objData?.omeMeetQualification[0]?.omeQualificationBonus?.length === 0)) {
          setErrors({ ...errorState, error: 'At least one Bonus Event Rule is required' });
        }
        else {
          if (omeMeetState.route !== '') {
            setSearchMeetState({ ...searchMeetState, showMeetHeader: true });
            navigate(NavLinks.MEET_HOST_MANAGEMENT, { state: { tryGetOmeMeet: true } });
          }
          else {
            navigate(MyMeetsNavLinks.MEET_HOST, { state: { selectOrgUnitState: selectOrgUnitState, myMeetsFiltersState: myMeetsFiltersState } });
          }
        }
      }
      else if (state.submitButton === HostRegistrationConstants.BACK) {
        if (omeMeetState?.objData?.omeMeetEligibility[0]?.bonusEvents === true && (omeMeetState?.objData?.omeMeetQualification[0] === undefined ||
          omeMeetState?.objData?.omeMeetQualification[0]?.omeQualificationBonus?.length === 0)) {
          setErrors({ ...errorState, error: 'At least one Bonus Event Rule is required' });
        }
        else {
          if (omeMeetState.route !== '') {
            navigate(NavLinks.MEET_HOST_INFO);
          }
          else {
            navigate(HostNavLinks.OME_MEET_INFO);
          }
        }
      }
      else if (state.submitButton === HostRegistrationConstants.ADD + 'Coach') {
        if (omeMeetState.route !== '') {
          navigate(NavLinks.MEET_HOST_ELIGIBILITY_COACH_PASSES, { state: { eligibilityFormState: formState } });
        }
        else {
          navigate(HostNavLinks.OME_MEET_ELIGIBILITY_COACH_PASSES, { state: { eligibilityFormState: formState } });
        }
      }
      else if (state.submitButton === HostRegistrationConstants.ADD + 'Bonus') {
        if (omeMeetState.route !== '') {
          navigate(NavLinks.MEET_HOST_ELIGIBILITY_BONUS_EVENTS, { state: { eligibilityFormState: formState } });
        }
        else {
          navigate(HostNavLinks.OME_MEET_ELIGIBILITY_BONUS_EVENTS, { state: { eligibilityFormState: formState } });
        }
      }
      else if (state.submitButton === HostRegistrationConstants.EDIT + 'Coach') {
        if (omeMeetState.route !== '') {
          navigate(NavLinks.MEET_HOST_ELIGIBILITY_COACH_PASSES, {
            state: {
              eligibilityFormState: formState,
              omeMeetCoachPassId: state.omeMeetCoachPassId
            }
          });
        }
        else {
          navigate(HostNavLinks.OME_MEET_ELIGIBILITY_COACH_PASSES, {
            state: {
              eligibilityFormState: formState,
              omeMeetCoachPassId: state.omeMeetCoachPassId
            }
          });
        }
      }
      else if (state.submitButton === HostRegistrationConstants.EDIT + 'Bonus') {
        if (omeMeetState.route !== '') {
          navigate(NavLinks.MEET_HOST_ELIGIBILITY_BONUS_EVENTS, {
            state: {
              eligibilityFormState: formState,
              omeMeetQualificationId: state.omeMeetQualificationId,
              qualificationBonusId: state.qualificationBonusId
            }
          });
        }
        else {
          navigate(HostNavLinks.OME_MEET_ELIGIBILITY_BONUS_EVENTS, {
            state: {
              eligibilityFormState: formState,
              omeMeetQualificationId: state.omeMeetQualificationId,
              qualificationBonusId: state.qualificationBonusId
            }
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting, omeMeetState.isSaving, omeMeetEntryState.isSaving, state]);

  function allowEdits() {
    if (omeMeetState.objData?.status === Constants.WORKFLOW_STATUS_CREATED) {
      if (omeMeetDateValidationState.objData?.isCurrentDateBeforeMeetEntryStartDate === true) {
        return true;
      }
      return false;
    }

    return true;
  }

  return {
    state,
    modalState,
    omeMeetState,
    omeMeetEntryState,
    formState,
    errorState,
    handleSubmit,
    onFormValueChanged,
    onNextButtonClicked,
    onSaveAndExitButtonClicked,
    onBackButtonClicked,
    onAddCoachPassRuleClicked,
    onEditCoachPassRuleClicked,
    onDeleteCoachPassRuleClicked,
    onDeleteCoachPassRule,
    onAddBonusEventRuleClicked,
    onEditBonusEventRuleClicked,
    onDeleteBonusEventRuleClicked,
    onDeleteBonusEventRule,
    formStateHasBeenSaved,
    onModalCanceled,
    warningModalState,
    onWarningModalCanceled,
    onSaveAndDelete,
    allowEdits,
    MAX_RELAY_TEAM_ENTRIES_PER_EVENT
  };
};

export default useOmeEligibilityNonHQ;